import { motion } from "framer-motion";
import { reveal, revealContainer, viewportOnce, scrollToId } from "../../lib/motion";

const CATEGORIES = [
  {
    label: "Individual Lunches",
    items: [
      { name: "Onigiri Lunch Box", price: "NZ$19", desc: "Two rice balls, karaage, tamagoyaki, salad, and pickles" },
      { name: "Premium Onigiri Lunch Box", price: "NZ$23", desc: "Two rice balls, karaage, tamagoyaki, two seasonal side dishes, and salad" },
      { name: "Karaage Bento", price: "NZ$22", desc: "Japanese fried chicken, rice, tamagoyaki, salad, and side dish" },
      { name: "Vegetarian Bento", price: "NZ$20", desc: "Seasonal vegetable dishes, rice, salad, and side dishes" },
    ],
  },
  {
    label: "Donburi",
    items: [
      { name: "Teriyaki Chicken Don", price: "NZ$20", desc: "Teriyaki chicken on rice" },
      { name: "Beef Gyudon", price: "NZ$22", desc: "Soy-braised beef and onion on rice with pickled ginger" },
      { name: "Teriyaki Salmon Don", price: "NZ$25", desc: "Teriyaki salmon on rice with side dish" },
      { name: "Teriyaki Tofu Don", price: "NZ$20", desc: "Teriyaki tofu on rice with salad" },
      { name: "Chicken Katsu Don", price: "NZ$22", desc: "Chicken katsu on rice" },
      { name: "Chicken Katsu with Egg", price: "NZ$25", desc: "Chicken katsu & omelet on rice" },
    ],
  },
  {
    label: "Sharing Platters",
    items: [
      { name: "Onigiri Platter · 12 pcs", price: "NZ$55", desc: "Assorted popular onigiri" },
      { name: "Onigiri Platter · 24 pcs", price: "NZ$110", desc: "Perfect for meetings and group events" },
      { name: "Sushi Platter · 24 pcs", price: "NZ$60", desc: "Assorted sushi rolls for small meetings and gatherings" },
      { name: "Sushi Platter · 40 pcs", price: "NZ$92", desc: "A generous assorted sushi platter for events and group catering" },
      { name: "Premium Sushi Platter · 40 pcs", price: "NZ$110", desc: "A premium platter for special occasions and hosting" },
      { name: "Vegetarian Sushi Platter · 24 pcs", price: "NZ$50", desc: "A fresh vegetable-based sushi selection" },
      { name: "Karaage Platter", price: "NZ$45", desc: "20–23 pcs · Juicy Japanese fried chicken" },
      { name: "Tamagoyaki Platter", price: "NZ$28", desc: "Japanese rolled omelette, cut into bite-sized pieces" },
      { name: "Japanese Side Platter", price: "NZ$36", desc: "Edamame, kinpira, potato salad, and seasonal Japanese side dishes" },
    ],
  },
];

const ONIGIRI_FLAVOURS = [
  "Salmon (Grilled Salty)",
  "Salmon Teriyaki",
  "Tuna Mayo",
  "Spicy Tuna",
  "Pickled Plum",
  "Miso Eggplant",
  "Chicken Teriyaki",
  "Beef Tsukudani (Sweet Soy)",
];

const PACKAGES = [
  { name: "Meeting Set A", price: "NZ$108", meta: "For 5–8 people", desc: "12 onigiri, small karaage platter, tamagoyaki, and 1 side dish" },
  { name: "Meeting Set B", price: "NZ$185", meta: "For 8–12 people", desc: "24 onigiri, karaage platter, tamagoyaki, and 2 Japanese side dishes" },
  { name: "Office Lunch Set", price: "NZ$20–24 pp", meta: "Minimum 10 people", desc: "A tailored combination of onigiri, donburi, and bento options to suit your budget and group size" },
  { name: "Event Finger Food Set", price: "NZ$17–22 pp", meta: "Minimum 15 people", desc: "Easy-to-eat items for casual events and gatherings" },
];

const ADDONS = [
  { name: "Miso Soup", price: "NZ$4.50 pp" },
  { name: "Edamame", price: "NZ$9" },
  { name: "Extra Rice", price: "NZ$3.50" },
  { name: "Tea / Soft Drinks", price: "On request" },
];

const PERFECT_FOR = [
  "Office lunches",
  "Meetings",
  "Corporate catering",
  "Birthdays & home parties",
  "Community events",
  "Small private gatherings",
];

const NOTES = [
  "All prices are in NZD.",
  "Menu items may change depending on ingredient availability.",
  "Sushi platter selections may vary depending on seasonal ingredients.",
  "Vegetarian options are available.",
  "We do our best to accommodate allergies, but food is prepared in the same kitchen environment.",
];

const PriceRow = ({ item }) => (
  <motion.div variants={reveal} className="py-5 border-b border-border">
    <div className="flex items-baseline justify-between gap-4">
      <h4 className="font-serif text-xl md:text-2xl text-ink">{item.name}</h4>
      <span className="font-serif text-lg md:text-xl text-gold whitespace-nowrap">{item.price}</span>
    </div>
    {item.desc && <p className="mt-1.5 text-ink/55 text-sm leading-relaxed max-w-lg">{item.desc}</p>}
  </motion.div>
);

const CategoryBlock = ({ cat }) => (
  <motion.div
    variants={revealContainer}
    initial="hidden"
    whileInView="show"
    viewport={viewportOnce}
    className="break-inside-avoid mb-12"
  >
    <motion.span variants={reveal} className="label-caps text-sage block mb-2">
      {cat.label}
    </motion.span>
    <div className="border-t border-ink/20">
      {cat.items.map((it) => (
        <PriceRow key={it.name} item={it} />
      ))}
    </div>
  </motion.div>
);

export const FullMenu = () => (
  <section id="full-menu" data-testid="full-menu-section" className="bg-surface py-24 md:py-32">
    <div className="mx-auto max-w-[1400px] px-6 md:px-10">
      <div className="max-w-3xl mb-16">
        <span className="label-caps text-gold">Full Catering Menu</span>
        <h2 className="mt-4 font-serif text-4xl md:text-5xl lg:text-6xl text-ink leading-[1.05]">
          The complete <span className="italic text-gold">table</span>
        </h2>
        <p className="mt-6 text-ink/60 leading-relaxed">
          From the onigiri and donburi loved in Mount Eden, we now offer a pre-order catering
          service for office lunches, meetings, events, and private gatherings.
        </p>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={viewportOnce}
        transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
        data-testid="custom-order-banner"
        className="mb-16 bg-charcoal rounded-2xl px-8 py-8 md:px-12 md:py-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6"
      >
        <div>
          <span className="label-caps text-gold">Bespoke Catering</span>
          <h3 className="mt-2 font-serif text-3xl md:text-4xl text-cream leading-tight">
            Custom Made Orders <span className="italic text-gold">Available</span>
          </h3>
          <p className="mt-3 text-cream/60 max-w-xl leading-relaxed">
            Have something specific in mind? We tailor menus to your event, budget and dietary
            needs. Please make an enquiry and we&rsquo;ll craft it for you.
          </p>
        </div>
        <button
          onClick={() => scrollToId("contact")}
          data-testid="custom-order-enquire-btn"
          className="shrink-0 rounded-full bg-gold px-8 py-3.5 text-charcoal font-medium hover:bg-cream transition-colors duration-300"
        >
          Make an Enquiry
        </button>
      </motion.div>

      {/* Individual + Donburi + Sharing in balanced columns */}
      <div className="lg:columns-2 lg:gap-16">
        {CATEGORIES.map((cat) => (
          <CategoryBlock key={cat.label} cat={cat} />
        ))}

        {/* Onigiri flavours */}
        <motion.div
          variants={revealContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="break-inside-avoid mb-12"
        >
          <motion.div variants={reveal} className="flex items-baseline justify-between mb-2">
            <span className="label-caps text-sage">Onigiri</span>
            <span className="font-serif text-lg text-gold">NZ$5 each</span>
          </motion.div>
          <motion.div variants={reveal} className="border-t border-ink/20 pt-5 grid grid-cols-2 gap-x-6 gap-y-3">
            {ONIGIRI_FLAVOURS.map((f) => (
              <span key={f} className="font-serif text-lg text-ink">
                {f}
              </span>
            ))}
          </motion.div>
          <motion.p variants={reveal} className="mt-4 text-ink/45 text-xs italic">
            Fillings may vary depending on availability.
          </motion.p>
        </motion.div>
      </div>

      {/* Catering Packages */}
      <div className="mt-6">
        <span className="label-caps text-gold block mb-8">Catering Packages</span>
        <motion.div
          variants={revealContainer}
          initial="hidden"
          whileInView="show"
          viewport={viewportOnce}
          className="grid grid-cols-1 md:grid-cols-2 gap-px bg-ink/15 border border-ink/15"
        >
          {PACKAGES.map((p) => (
            <motion.div
              key={p.name}
              variants={reveal}
              data-testid={`package-${p.name}`}
              className="bg-cream p-8 flex flex-col gap-3"
            >
              <div className="flex items-baseline justify-between gap-4">
                <h3 className="font-serif text-2xl text-ink">{p.name}</h3>
                <span className="font-serif text-xl text-gold whitespace-nowrap">{p.price}</span>
              </div>
              <span className="label-caps text-sage">{p.meta}</span>
              <p className="text-ink/55 text-sm leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>

      {/* Add-ons + Perfect For */}
      <div className="mt-20 grid grid-cols-1 md:grid-cols-2 gap-16">
        <div>
          <span className="label-caps text-gold block mb-6">Add-ons</span>
          <div className="border-t border-ink/20">
            {ADDONS.map((a) => (
              <div key={a.name} className="flex items-baseline justify-between py-4 border-b border-border">
                <span className="font-serif text-xl text-ink">{a.name}</span>
                <span className="font-serif text-lg text-gold">{a.price}</span>
              </div>
            ))}
          </div>
        </div>
        <div>
          <span className="label-caps text-gold block mb-6">Perfect For</span>
          <div className="flex flex-wrap gap-3">
            {PERFECT_FOR.map((p) => (
              <span key={p} className="border border-ink/20 rounded-full px-5 py-2 text-ink/80 text-sm">
                {p}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Notes */}
      <div className="mt-20 border-t border-ink/15 pt-8">
        <span className="label-caps text-ink/40 block mb-4">Notes</span>
        <ul className="space-y-2 max-w-3xl">
          {NOTES.map((n) => (
            <li key={n} className="text-ink/50 text-sm flex gap-3">
              <span className="text-gold">·</span> {n}
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);
